import React, { forwardRef } from 'react';
import { ColorPicker } from '@progress/kendo-react-inputs';
import { convertRGBtoHex, convertHexToRGB } from 'src/shared/helpers/colors';

/**
 * @typedef {Object} AdditionalColorPickerProps
 * @property {string} value Selected color value.
 * @property  {'hex' | 'rgb'} [format='hex'] A color value format. "hex" by default.
 *
 * @typedef {Omit<React.ComponentProps<ColorPicker>, 'value'>} TelerikColorPickerProps
 *
 * @param {TelerikColorPickerProps & AdditionalColorPickerProps} props
 * @param {React.Ref<HTMLDivElement>} ref
 * @returns {React.ReactElement}
 */
const BaseColorPicker = ({ onChange, value, format = 'hex', ...rest }, ref) => {
  const handleChange = (e) => {
    if (format === 'hex') {
      const { value } = e;
      onChange({ ...e, value: convertRGBtoHex(value) });
      return;
    }
    onChange(e);
  };

  const formatted = format === 'hex' ? convertHexToRGB(value) : value;

  return (
    <ColorPicker
      ref={ref}
      view='gradient'
      gradientSettings={{
        opacity: false,
      }}
      onChange={handleChange}
      value={formatted}
      {...rest}
    />
  );
};

/**
 * A base color picker component with various options and handlers
 *
 * @param {TelerikColorPickerProps & AdditionalColorPickerProps} props Component props. Extends Telerik ColorPicker's props.
 * @param {React.Ref<HTMLDivElement>} ref
 * @returns {React.ReactElement} Base color picker component.
 */
const ForwardColorPicker = forwardRef(BaseColorPicker);

export default ForwardColorPicker;
